import type { IpaLineItem } from "../../shared/api";
import { Check } from "lucide-react";
import { formatNumber } from "../../shared/versions";
import { cn } from "@/lib/utils";
import { navigate } from "@/lib/nav";

// Grafikler yalnız çizimdir (ui.md §12.9): oranlar çubuk genişliği içindir, ekranda yazan her sayı çağıranın verdiği API değeridir.
const clamp = (pct: number) => Math.max(0, Math.min(100, pct));

/** Yatay ilerleme çubuğu. `role=progressbar` korunur (E5); %100'de durur. */
export function Meter({ label, pct, valueText }: { label: string; pct: number | null; valueText: string }) {
  return (
    <div role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct ?? undefined} aria-valuetext={valueText} className="h-2 w-full overflow-hidden rounded-full bg-chart-track">
      <div className="h-full rounded-full bg-primary" style={{ width: `${clamp(pct ?? 0)}%` }} />
    </div>
  );
}

/** Halka (72 px, kalınlık 8), 12 yönünden saat yönüne. `pct` yoksa yalnız zemin. */
export function RingMeter({ pct }: { pct: number | null }) {
  const r = 32, c = 2 * Math.PI * r;
  return (
    <span aria-hidden="true" className="relative inline-flex shrink-0">
    <svg data-slot="ring-meter" viewBox="0 0 72 72" className="size-[72px] -rotate-90">
      <circle cx="36" cy="36" r={r} fill="none" strokeWidth="8" className="stroke-chart-track" />
      {pct !== null && pct > 0 && <circle cx="36" cy="36" r={r} fill="none" strokeWidth="8" strokeLinecap="round" className="stroke-primary" strokeDasharray={c} strokeDashoffset={c * (1 - clamp(pct) / 100)} />}
    </svg>
    {pct !== null && pct >= 100 && <Check data-slot="ring-check" className="absolute inset-0 m-auto size-6 text-primary" />}
    </span>
  );
}

export type Segment = { label: string; count: number; color: "critical" | "warning" | "info" | "primary" | "track"; /** verilirse efsane kalemi bu adrese giden bağlantı olur */ href?: string };
const FILL: Record<Segment["color"], string> = { critical: "bg-chart-critical", warning: "bg-chart-warning", info: "bg-chart-info", primary: "bg-primary", track: "bg-chart-track" };

/** Parçalı çubuk + efsane. Sıfır parça çizilmez ama efsanede sayısıyla yazar; sıra çağıranındır. */
export function SegmentBar({ segments, className, hideCounts, legend = true }: { segments: Segment[]; className?: string; hideCounts?: boolean; legend?: boolean }) {
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div aria-hidden="true" data-slot="segment-bar" className="flex h-2 w-full gap-0.5 overflow-hidden rounded-full">
        {segments.filter((s) => s.count > 0).map((s) => <div key={s.label} className={FILL[s.color]} style={{ flexGrow: s.count, flexBasis: 0 }} />)}
      </div>
      {legend && <ul className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
        {segments.map((s) => (
          <li key={s.label} className="flex items-center gap-1.5"><span aria-hidden="true" className={cn("size-2.5 rounded-sm", FILL[s.color])} />{s.href ? <a href={s.href} onClick={(e) => navigate(e, s.href!)} className="rounded-sm text-primary underline-offset-4 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring">{s.label}</a> : s.label}{!hideCounts && <> <b className="font-semibold tabular-nums">{formatNumber(s.count)}</b></>}</li>
        ))}
      </ul>}
    </div>
  );
}

/** Satır başına etiket · çubuk · sayı; büyükten küçüğe. Çubuk `max`'a (verilmezse en büyük değere) göredir; `value: null` çubuk yerine "Yüklenmedi" yazar. */
export function BarList({ items, max: scale }: { items: { label: string; value: number | null }[]; max?: number }) {
  const rows = [...items].sort((a, b) => (b.value ?? -1) - (a.value ?? -1));
  const max = scale ?? rows[0]?.value ?? 0;
  return (
    <ul data-slot="bar-list" className="flex flex-col gap-2 text-sm">
      {rows.map((it) => (
        <li key={it.label} className="grid grid-cols-[12rem_1fr_5rem] items-center gap-3">
          <span className="truncate">{it.label}</span>
          {it.value === null ? <span className="col-span-2 text-muted-foreground">Yüklenmedi</span> : <>
            <span aria-hidden="true" className="h-2 overflow-hidden rounded-full bg-chart-track"><span className={cn("block h-full rounded-full bg-primary", it.value > 0 && "min-w-1")} style={{ width: `${max > 0 ? (it.value / max) * 100 : 0}%` }} /></span>
            <span className="text-right tabular-nums">{formatNumber(it.value)}</span>
          </>}
        </li>
      ))}
    </ul>
  );
}

type MonthPoint = IpaLineItem["months"][number];
const MONTH_SHORT = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const monthName = (m: string) => MONTH_SHORT[Number(m.slice(5, 7)) - 1] ?? m;

/** Birikimli gerçekleşme: basamaklı çizgi (ay başına bir nokta) ve kesikli taahhüt çizgisi, 160 px. Y: 0 → taahhüt; ekranda yazan sayılar API'den, hesap yok. */
export function CumulativeLine({ label, months, commitmentKg }: { label: string; months: MonthPoint[]; commitmentKg: number }) {
  const n = months.length;
  if (n === 0) return null;
  const scale = Math.max(commitmentKg, ...months.map((m) => m.cumulativeKg)) || 1;
  const x = (i: number) => ((i + 0.5) / n) * 100, y = (kg: number) => 100 - (kg / scale) * 100;
  const d = months.map((m, i) => i === 0 ? `M${x(0)},${y(m.cumulativeKg)}` : `H${x(i)}V${y(m.cumulativeKg)}`).join("");
  const last = months[n - 1]!;
  return (
    <figure data-slot="cumulative-line" role="img" aria-label={`${label}: ${monthName(last.month)} itibarıyla ${formatNumber(last.cumulativeKg)} kg, taahhüt ${formatNumber(commitmentKg)} kg`} className="relative m-0 flex h-40 flex-col text-xs text-muted-foreground">
      <span aria-hidden="true" className="absolute left-0 top-0">İhracat, birikimli</span>
      <div aria-hidden="true" className="relative mt-5 flex-1">
        <span className="absolute right-0 -translate-y-full pb-0.5" style={{ top: `${y(commitmentKg)}%` }}>{`Taahhüt ${formatNumber(commitmentKg)} kg`}</span>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 size-full overflow-visible">
          <line x1="0" x2="100" y1={y(commitmentKg)} y2={y(commitmentKg)} strokeDasharray="4 3" vectorEffect="non-scaling-stroke" className="stroke-muted-foreground" strokeWidth="1" />
          <path d={d} fill="none" vectorEffect="non-scaling-stroke" className="stroke-primary" strokeWidth="2" />
        </svg>
        {months.map((m, i) => m.kg > 0 && <span key={m.month} className="absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary" style={{ left: `${x(i)}%`, top: `${y(m.cumulativeKg)}%` }} />)}
      </div>
      <div aria-hidden="true" className="mt-1 grid" style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }}>
        {months.map((m) => <span key={m.month} className="text-center">{monthName(m.month)}</span>)}
      </div>
    </figure>
  );
}
